import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Loader2, BookOpen, Lock, User, ShieldCheck } from 'lucide-react'

export default function Login() {
  const navigate = useNavigate()
  const { login } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaRequired, setMfaRequired] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(email, password, mfaRequired ? mfaCode : undefined)

      if (result.mfaRequired) {
        setMfaRequired(true)
        setLoading(false)
        return
      }

      navigate('/', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed'
      setError(message)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-acad-500 via-acad-600 to-acad-700 px-4">
      {/* Decorative background circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-acad-400 opacity-20" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-acad-800 opacity-15" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-acad-300 opacity-10" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo / University branding area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Academic Discovery Portal</h1>
          <p className="text-acad-100 mt-2">Sign in to access scholarly resources</p>
        </div>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error display */}
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}

            {!mfaRequired ? (
              <>
                {/* Username field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      type="text"
                      required
                      autoComplete="username"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500 transition"
                      placeholder="Enter your username"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500 transition"
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                  </div>
                </div>
              </>
            ) : (
              /* MFA code field */
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  A verification code has been sent to your device. Enter it below to complete sign-in.
                </p>
                <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Verification code
                </label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="mfa-code"
                    type="text"
                    required
                    autoFocus
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500 transition tracking-widest text-center text-lg"
                    placeholder="000000"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-acad-500 hover:bg-acad-600 active:bg-acad-700 text-white font-semibold rounded-lg shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{mfaRequired ? 'Verifying...' : 'Signing in...'}</span>
                </>
              ) : (
                <span>{mfaRequired ? 'Verify' : 'Sign In'}</span>
              )}
            </button>

            {mfaRequired && (
              <button
                type="button"
                onClick={() => {
                  setMfaRequired(false)
                  setMfaCode('')
                  setError('')
                }}
                className="w-full text-sm text-gray-500 hover:text-acad-600 transition"
              >
                Back to sign in
              </button>
            )}
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-acad-100/80 text-xs mt-6">
          SecureEmbed Academic Discovery Portal
        </p>
      </div>
    </div>
  )
}
